// ParxlShippingModal.tsx
//
// SG (Parxl) booking dialog — the Singapore counterpart of
// SFInternationalShippingModal. SG is the one intl region SF does not carry
// (config/intlRegions.ts `carrier`), so it books through /parxl/create and the
// label comes back as PDF BYTES we proxy, not a hosted URL like SF's.
//
// Why this dialog looks different from the SF one:
//   * WEIGHT IS BILLABLE AND IS THE MAIN INPUT. Parxl charges on a step function
//     (≤1.5kg NT$290 · 2kg 330 · 2.5kg 380 · 3kg 420 …) against a NT$300 customer
//     fee, so a few hundred grams flips the parcel from profit to loss. SF, by
//     contrast, re-weighs at pickup and reconciles in billing, which is why that
//     lane can declare a flat 5kg placeholder. The warehouse types the scale
//     reading here and sees the cost before committing.
//   * No region/district tiers: SG needs only a 6-digit postcode.
//   * No cert/ID field: Parxl asks for no recipient ID for SG.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, Stack, TextField, Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import api from '../services/api';
import { INTL_REGIONS } from '../config/intlRegions';

export interface ParxlShippingResult {
  trackingNo: string;
  merchantOrderNo: string;
  weightKg: number;
  env: string;
}

interface ParxlShippingModalProps {
  open: boolean;
  onClose: () => void;
  order: any; // UnifiedOrder with .original pointing at the storefront orders row
  enqueueSnackbar: (message: string, options?: any) => void;
  onShippingPrepared?: (data: ParxlShippingResult) => void;
}

const REGION = INTL_REGIONS.SG;

// The storefront has no postcode column — checkout appends it as the address's
// canonical last line ("Singapore 560407"). Take the LAST 6-digit run so a
// leading block number cannot win.
const extractPostcode = (address: string): string => {
  const m = String(address || '').match(/\b\d{6}\b/g);
  return m && m.length ? m[m.length - 1] : '';
};

const ParxlShippingModal: React.FC<ParxlShippingModalProps> = ({
  open, onClose, order, enqueueSnackbar, onShippingPrepared,
}) => {
  const orig = order?.original || {};
  const existingTracking: string | null = orig.parxl_tracking_no || null;

  const initialAddress = orig.shipping_address || order?.shippingAddress || '';

  const [weightKg, setWeightKg] = useState('1');
  const [lengthCm, setLengthCm] = useState('30');
  const [widthCm, setWidthCm] = useState('25');
  const [heightCm, setHeightCm] = useState('8');
  const [postcode, setPostcode] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  const [rate, setRate] = useState<{ amount: number; currency: string } | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ParxlShippingResult | null>(null);

  // Reset when a different order is selected while mounted.
  useEffect(() => {
    if (!open) return;
    setName(orig.customer_name || order?.customerName || '');
    setPhone(orig.customer_phone || order?.customerPhone || '');
    setEmail(orig.customer_email || order?.customerEmail || '');
    setAddress(initialAddress);
    setPostcode(extractPostcode(initialAddress));
    setWeightKg('1');
    setLengthCm('30'); setWidthCm('25'); setHeightCm('8');
    setRate(null); setError(null); setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.orderId]);

  const validation = useMemo(() => {
    if (!/^\d{6}$/.test(postcode)) return '新加坡郵遞區號必須是 6 位數字';
    if (!name.trim()) return '請輸入收件人姓名';
    if (!phone.replace(/\D/g, '')) return '請輸入收件人電話';
    if (!email.trim()) return 'Parxl 規定必須提供收件人 Email';
    if (!address.trim()) return '請輸入收件地址';
    const w = Number(weightKg);
    if (!Number.isFinite(w) || w <= 0) return '請輸入包裹重量 (kg)';
    if (w > 30) return '重量超過 30kg，超出 Parxl TW→SG 費率表範圍';
    return null;
  }, [postcode, name, phone, email, address, weightKg]);

  const checkRate = useCallback(async () => {
    if (!/^\d{6}$/.test(postcode)) { setError('請先填入 6 位數郵遞區號'); return; }
    setRateLoading(true); setError(null);
    try {
      const resp = await api.get(`/api/storefront/orders/${encodeURIComponent(order.orderId)}/parxl/rate`, {
        params: { weightKg: Number(weightKg) || 1, postcode, lengthCm, widthCm, heightCm },
      });
      setRate({ amount: resp.data?.estimatedRate, currency: resp.data?.currency || 'TWD' });
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
      setRate(null);
    } finally {
      setRateLoading(false);
    }
  }, [order?.orderId, weightKg, postcode, lengthCm, widthCm, heightCm]);

  const handleBook = useCallback(async () => {
    if (validation) { setError(validation); return; }
    setBooking(true); setError(null);
    try {
      const resp = await api.post(`/api/storefront/orders/${encodeURIComponent(order.orderId)}/parxl/create`, {
        parcel: {
          weightKg: Number(weightKg),
          lengthCm: Number(lengthCm),
          widthCm: Number(widthCm),
          heightCm: Number(heightCm),
        },
        receiver: { name, phone, email, address, postcode },
      });
      const data: ParxlShippingResult = {
        trackingNo: resp.data.trackingNo,
        merchantOrderNo: resp.data.merchantOrderNo,
        weightKg: resp.data.weightKg,
        env: resp.data.env,
      };
      setResult(data);
      enqueueSnackbar(`Parxl 單已建立 ${data.trackingNo}`, { variant: 'success', autoHideDuration: 6000 });
      onShippingPrepared?.(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBooking(false);
    }
  }, [validation, order?.orderId, weightKg, lengthCm, widthCm, heightCm, name, phone, email, address, postcode, enqueueSnackbar, onShippingPrepared]);

  // The label is PDF bytes behind adminAuth, so it cannot simply be window.open'd
  // — fetch it as a blob with the auth header, then open the object URL.
  const openLabel = useCallback(async () => {
    try {
      const resp = await api.get(`/api/storefront/orders/${encodeURIComponent(order.orderId)}/parxl/label`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) enqueueSnackbar('請允許彈出視窗以開啟 Parxl 面單', { variant: 'warning' });
      // Revoke late so the new tab has finished loading the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      enqueueSnackbar(`載入 Parxl 面單失敗：${e?.response?.data?.error || e.message}`, { variant: 'error' });
    }
  }, [order?.orderId, enqueueSnackbar]);

  const done = !!(result || existingTracking);
  const trackingNo = result?.trackingNo || existingTracking;

  return (
    <Dialog open={open} onClose={booking ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <LocalShippingIcon />
          <span>{REGION.flag} {REGION.nameZh}國際出貨 (Parxl) — 訂單 #{order?.orderNumber || order?.orderId}</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <Alert severity="success">
            <AlertTitle>Parxl 單已建立</AlertTitle>
            追蹤號碼：<strong>{trackingNo}</strong>
            {result?.weightKg ? <> · 申報重量 {result.weightKg} kg</> : null}
            {result?.env === 'uat' && (
              <Box sx={{ mt: 1 }}>
                <Chip size="small" color="warning" label="UAT 測試環境 — 這不是真實包裹" />
              </Box>
            )}
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{REGION.nameZh}由 Parxl 配送（末端 Ninjavan SG）</AlertTitle>
              {REGION.customsNote} 送出後系統會建立託運單並可直接列印面單。
            </Alert>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              包裹重量與尺寸
            </Typography>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              運費依<strong>重量級距</strong>計費（≤1.5kg NT$290 · 2kg 330 · 2.5kg 380 · 3kg 420 · 4kg 510 · 5kg 600），
              向客人只收 NT$300 — 請務必<strong>用磅秤實際量測</strong>，不要憑感覺填。
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="重量 (kg)" value={weightKg} required
                  onChange={e => { setWeightKg(e.target.value); setRate(null); }}
                  inputProps={{ inputMode: 'decimal' }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="長 (cm)" value={lengthCm}
                  onChange={e => { setLengthCm(e.target.value); setRate(null); }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="寬 (cm)" value={widthCm}
                  onChange={e => { setWidthCm(e.target.value); setRate(null); }} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="高 (cm)" value={heightCm}
                  onChange={e => { setHeightCm(e.target.value); setRate(null); }} />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" onClick={checkRate} disabled={rateLoading}
                sx={{ textTransform: 'none' }}>
                {rateLoading ? <CircularProgress size={16} /> : '試算運費'}
              </Button>
              {rate && (
                <Chip
                  color={rate.amount > 300 ? 'error' : 'success'}
                  label={`成本 ${rate.currency} ${rate.amount} · 客人付 300 · ${rate.amount > 300 ? `虧 ${rate.amount - 300}` : `賺 ${300 - rate.amount}`}`}
                />
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              收件人 ({REGION.nameZh})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="姓名 Name" value={name} required
                  onChange={e => setName(e.target.value)}
                  helperText="會拆成 first / last name 印在面單上" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label={`手機 Phone (+${REGION.phoneAreaCode})`} value={phone} required
                  onChange={e => setPhone(e.target.value)}
                  helperText={`新加坡手機 8 碼，免加 ${REGION.phoneAreaCode}`} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Email" value={email} required
                  onChange={e => setEmail(e.target.value)}
                  helperText="Parxl 規定必填（出貨通知寄給客人）" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="郵遞區號 Postcode" value={postcode} required
                  onChange={e => { setPostcode(e.target.value.replace(/\D/g, '').slice(0, 6)); setRate(null); }}
                  helperText="6 位數字，Parxl 沒有這個不能建單" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="地址 Address（英文）" value={address} required
                  multiline minRows={2} onChange={e => setAddress(e.target.value)}
                  helperText="系統會自動移除結尾的 Singapore + 郵遞區號（那兩項單獨送出）" />
              </Grid>
            </Grid>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={booking} sx={{ textTransform: 'none' }}>
          {done ? '關閉' : '取消'}
        </Button>
        {done ? (
          <Button variant="contained" startIcon={<PrintIcon />} onClick={openLabel}
            sx={{ textTransform: 'none' }}>
            列印 Parxl 面單
          </Button>
        ) : (
          <Button variant="contained" onClick={handleBook} disabled={booking || !!validation}
            startIcon={booking ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />}
            sx={{ textTransform: 'none' }}>
            {booking ? '建立中…' : '建立 Parxl 託運單'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ParxlShippingModal;
